import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, TouchEvent } from "react";
import { createPortal } from "react-dom";
import {
  fetchConfig,
  fetchMarketing,
  fetchOrders,
  fetchProductGroups,
  fetchProductRecommendations,
  fetchProducts,
  fetchSettlements,
  fetchOrderDetails,
  logout,
  submitOrder,
  updatePointContext,
} from "../api";
import {
  formatProductName,
  resolveMinQuantityRounded,
  resolveStep,
} from "../utils/productQuantity";
import { toDateInputValue } from "../utils/dateInput";
import {
  formatSalesDaysLabel,
  getSalesDayFromDate,
  isProductAvailableOnDay,
} from "../utils/salesDays";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatExistingOrderMessage,
  formatOrderLabel,
  formatOverdue,
  getOrderKey,
  getOrderStatusLabel,
} from "../utils/orderDisplay";
import {
  ProductGrid,
  ProductList,
  Pagination,
  QuantityInput,
} from "../components/catalog";
import { CartSummary } from "../components/cart";
import {
  CompanyLogo,
  IconCart,
  IconX,
  RangeSearchFilters,
  SectionHeader,
} from "../components/common";
import { OrdersGrid, OrdersListSection } from "../components/orders";
import { SettlementCard, SettlementsTable } from "../components/settlements";
import ProfileView from "./ProfileView";
import type {
  CartMap,
  Id,
  MarketingEntry,
  OrderDetailLine,
  OrderDetails,
  OrderListItem,
  Product,
  ProductGroup,
  RecommendationItem,
  SettlementItem,
  StatusMessage,
  TotalDiscountTier,
  UserSession,
} from "../types";

type CatalogViewMode = "grid" | "list";
type OrdersActivePage =
  | "home"
  | "catalog"
  | "my-orders"
  | "settlements"
  | "profile";

interface QuantityChangeOptions {
  source?: "catalog" | "recommendation" | string;
}

interface OrdersViewProps {
  user: UserSession;
  onLogout: () => void;
  onUserUpdate?: (nextUser: UserSession) => void;
}

const OrdersView = ({ user, onLogout, onUserUpdate }: OrdersViewProps) => {
  const STATUS_AUTO_DISMISS_MS = 6000;
  const EXISTING_ORDER_AUTO_DISMISS_MS = 8000;
  const [minOrderNet, setMinOrderNet] = useState(150);
  const [minItemQuantity, setMinItemQuantity] = useState(6);
  const [maxRecommendations, setMaxRecommendations] = useState(3);
  const [totalDiscountTiers, setTotalDiscountTiers] = useState<
    TotalDiscountTier[]
  >([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [groups, setGroups] = useState<ProductGroup[]>([]);
  const [cart, setCart] = useState<CartMap>({});
  const [notes, setNotes] = useState("");
  const [isCartVisible, setCartVisible] = useState(false);
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<Id | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetails | null>(null);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [orderDetailsError, setOrderDetailsError] = useState("");
  const [marketing, setMarketing] = useState<MarketingEntry[] | null>(null);
  const [settlements, setSettlements] = useState<SettlementItem[]>([]);
  const [status, setStatus] = useState<StatusMessage>({
    type: "",
    message: "",
  });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<CatalogViewMode>("list");
  const [groupFilter, setGroupFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [activePage, setActivePage] = useState<OrdersActivePage>("home");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersPageSize, setOrdersPageSize] = useState(50);
  const [ordersDateFrom, setOrdersDateFrom] = useState("");
  const [ordersDateTo, setOrdersDateTo] = useState("");
  const [ordersSearchQuery, setOrdersSearchQuery] = useState("");
  const [settlementsPage, setSettlementsPage] = useState(1);
  const [settlementsPageSize, setSettlementsPageSize] = useState(50);
  const [settlementsDateFrom, setSettlementsDateFrom] = useState("");
  const [settlementsDateTo, setSettlementsDateTo] = useState("");
  const [settlementsSearchQuery, setSettlementsSearchQuery] = useState("");
  const [pointChangeLoading, setPointChangeLoading] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [minDeliveryDate, setMinDeliveryDate] = useState("");
  const [hasExistingOrder, setHasExistingOrder] = useState(false);
  const [existingOrderMessage, setExistingOrderMessage] = useState("");
  const [recommendationsByProduct, setRecommendationsByProduct] = useState<
    Record<number, RecommendationItem[]>
  >({});
  const [recommendationsLoadingByProduct, setRecommendationsLoadingByProduct] =
    useState<Record<number, boolean>>({});
  const [lastModifiedProductId, setLastModifiedProductId] = useState<
    number | null
  >(null);
  const [isNavOpen, setNavOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState<{
    pointLabel: string;
    deliveryLabel: string;
    netAmount: string;
    grossAmount: string;
  } | null>(null);
  const [orderDetailQuantities, setOrderDetailQuantities] = useState<
    Record<string, number>
  >({});
  const topBarRef = useRef<HTMLElement | null>(null);
  const points = Array.isArray(user.points) ? user.points : [];
  const pointSwitchMessageRef = useRef<string | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const touchLastYRef = useRef<number | null>(null);
  const recommendationsCacheRef = useRef<Record<number, RecommendationItem[]>>(
    {},
  );
  const recommendationsInFlightRef = useRef<Set<number>>(new Set());
  const activePointId = user?.pointId ?? null;
  const activePointIdRef = useRef<number | null>(activePointId);

  const resolveMinQuantity = (product: Product | null | undefined) => {
    return resolveMinQuantityRounded(product, minItemQuantity);
  };

  useEffect(() => {
    recommendationsCacheRef.current = recommendationsByProduct;
  }, [recommendationsByProduct]);

  useEffect(() => {
    activePointIdRef.current = activePointId;
  }, [activePointId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (!status.message) {
      return;
    }

    const isExistingOrderStatus =
      status.message === existingOrderMessage ||
      status.message.includes("Zamówienie dla wybranego punktu");
    const shouldAutoDismiss = status.type !== "error" || isExistingOrderStatus;

    if (!shouldAutoDismiss) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => {
        setStatus({ type: "", message: "" });
      },
      isExistingOrderStatus
        ? EXISTING_ORDER_AUTO_DISMISS_MS
        : STATUS_AUTO_DISMISS_MS,
    );

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [status.message, status.type, existingOrderMessage]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (!existingOrderMessage) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setExistingOrderMessage("");
    }, EXISTING_ORDER_AUTO_DISMISS_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [existingOrderMessage]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const topBar = topBarRef.current;
    if (!topBar) {
      return;
    }

    const root = document.documentElement;
    const syncTopBarHeight = () => {
      const height = Math.ceil(topBar.getBoundingClientRect().height);
      if (height > 0) {
        root.style.setProperty("--top-bar-height", `${height}px`);
      }
    };

    syncTopBarHeight();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            syncTopBarHeight();
          })
        : null;

    resizeObserver?.observe(topBar);
    window.addEventListener("resize", syncTopBarHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", syncTopBarHeight);
    };
  }, []);

  const handlePointSwitch = async (event: ChangeEvent<HTMLSelectElement>) => {
    if (pointChangeLoading) {
      return;
    }

    const nextValue = event.target.value;
    const nextPointId = Number(nextValue);

    if (!Number.isInteger(nextPointId) || nextPointId <= 0) {
      setStatus({ type: "error", message: "Nieprawidłowy wybór punktu." });
      return;
    }

    const currentPointId = user.pointId == null ? null : Number(user.pointId);
    if (currentPointId !== null && nextPointId === currentPointId) {
      return;
    }

    setPointChangeLoading(true);
    setStatus({ type: "", message: "" });

    try {
      const updatedUser = await updatePointContext(nextPointId);
      if (updatedUser) {
        const activePointName =
          updatedUser.pointName ||
          updatedUser.points?.find((point) => point.id === updatedUser.pointId)
            ?.name ||
          null;
        pointSwitchMessageRef.current = activePointName;
        if (typeof onUserUpdate === "function") {
          onUserUpdate(updatedUser);
        } else {
          setStatus({
            type: "error",
            message:
              "Nie udało się odświeżyć danych użytkownika po zmianie punktu.",
          });
          pointSwitchMessageRef.current = null;
        }
      } else {
        setStatus({
          type: "error",
          message: "Nie udało się przełączyć punktu handlowego.",
        });
      }
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setPointChangeLoading(false);
    }
  };

  const getWarsawNow = () => {
    // Obsługa parametrów testowych: ?testDate=2024-02-10&testHour=10&testMinute=30
    const params = new URLSearchParams(window.location.search);
    const testDate = params.get("testDate");
    const testHour = params.get("testHour");
    const testMinute = params.get("testMinute");

    if (testDate) {
      const parsed = new Date(testDate);
      if (!Number.isNaN(parsed.getTime())) {
        const hour = testHour ? Number(testHour) : parsed.getHours();
        const minute = testMinute ? Number(testMinute) : parsed.getMinutes();
        return new Date(
          parsed.getFullYear(),
          parsed.getMonth(),
          parsed.getDate(),
          hour,
          minute,
          0,
          0,
        );
      }
    }

    const parts = new Intl.DateTimeFormat("pl-PL", {
      timeZone: "Europe/Warsaw",
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
      .formatToParts(new Date())
      .reduce<Record<string, string>>((acc, part) => {
        acc[part.type] = part.value;
        return acc;
      }, {});

    return new Date(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      0,
      0,
    );
  };

  const computeMinDeliveryDate = () => {
    const now = getWarsawNow();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekday = now.getDay(); // 0 niedziela, 1 pon
    const hour = now.getHours();
    const minute = now.getMinutes();

    // Sobota: do 11:00 -> wtorek, po 11:00 -> środa
    if (weekday === 6) {
      const beforeCutoff = hour < 11 || (hour === 11 && minute === 0);
      const daysToTuesday = 3; // sob -> wtorek
      const daysToWednesday = 4; // sob -> środa
      today.setDate(
        today.getDate() + (beforeCutoff ? daysToTuesday : daysToWednesday),
      );
      return toDateInputValue(today);
    }

    // Godzina graniczna 14:30
    const afterCutoff = hour > 14 || (hour === 14 && minute >= 30);

    // Minimum 2 dni do przodu, po 14:30 -> +1 dzień
    today.setDate(today.getDate() + 2);
    if (afterCutoff) {
      today.setDate(today.getDate() + 1);
    }

    // Pomij niedzielę i piątek jako dni dostawy
    while (today.getDay() === 0 || today.getDay() === 5) {
      today.setDate(today.getDate() + 1);
    }

    return toDateInputValue(today);
  };

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      const pendingPointName = pointSwitchMessageRef.current;
      if (!pendingPointName) {
        setStatus({ type: "", message: "" });
      }
      try {
        const [
          productsData,
          groupsData,
          ordersData,
          marketingData,
          settlementsData,
          configData,
        ] = await Promise.all([
          fetchProducts(),
          fetchProductGroups(),
          fetchOrders(),
          fetchMarketing(),
          fetchSettlements(),
          fetchConfig(),
        ]);

        if (!active) {
          return;
        }

        setProducts(productsData);
        setGroups(groupsData);
        setOrders(ordersData);
        setSelectedOrderId(null);
        setSelectedOrder(null);
        setOrderDetailsError("");
        setOrderDetailsLoading(false);
        setMarketing(marketingData);
        setSettlements(settlementsData);

        if (configData?.minOrderNet) {
          setMinOrderNet(configData.minOrderNet);
        }
        if (configData?.minItemQuantity) {
          setMinItemQuantity(configData.minItemQuantity);
        }
        if (configData?.maxRecommendations) {
          setMaxRecommendations(configData.maxRecommendations);
        }
        if (Array.isArray(configData?.totalDiscounts)) {
          setTotalDiscountTiers(configData.totalDiscounts);
        }

        const nextMessagePoint = pointSwitchMessageRef.current;
        if (nextMessagePoint) {
          setStatus({
            type: "success",
            message: `Aktywny punkt: ${nextMessagePoint}.`,
          });
          pointSwitchMessageRef.current = null;
        }
      } catch (err) {
        if (!active) {
          return;
        }
        pointSwitchMessageRef.current = null;
        setStatus({ type: "error", message: err.message });
        setSettlements([]);
      } finally {
        if (!active) {
          return;
        }
        setLoading(false);
      }
    };

    setCart({});
    setNotes("");
    setRecommendationsByProduct({});
    setRecommendationsLoadingByProduct({});
    recommendationsCacheRef.current = {};
    recommendationsInFlightRef.current.clear();
    load();

    return () => {
      active = false;
    };
  }, [user.pointId]);

  useEffect(() => {
    const initialize = () => {
      const minDate = computeMinDeliveryDate();
      setMinDeliveryDate(minDate);
      setDeliveryDate((prev) => (prev ? prev : minDate));
    };
    initialize();
    const interval = setInterval(() => {
      setMinDeliveryDate(computeMinDeliveryDate());
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!minDeliveryDate) {
      return;
    }
    setDeliveryDate((prev) => {
      if (!prev || prev < minDeliveryDate) {
        return minDeliveryDate;
      }
      return prev;
    });
  }, [minDeliveryDate]);
  const activeSalesDay = useMemo(
    () => getSalesDayFromDate(deliveryDate),
    [deliveryDate],
  );

  const cartItems = useMemo(() => {
    return Object.values(cart).map((item) => {
      const isAvailable = isProductAvailableOnDay(item, activeSalesDay);
      const lineNet = item.price * item.quantity;
      const vatRate = Number.isFinite(item.vatRate) ? item.vatRate : 0;
      const lineGross = lineNet * (1 + Math.max(vatRate, 0));
      return {
        ...item,
        displayName: item.displayName || formatProductName(item),
        isAvailable,
        lineNet,
        lineGross,
        vatRate,
      };
    });
  }, [cart, activeSalesDay]);

  const cartTotals = useMemo(() => {
    return cartItems.reduce(
      (sum, item) => {
        sum.net += item.lineNet;
        sum.gross += item.lineGross;
        return sum;
      },
      { net: 0, gross: 0 },
    );
  }, [cartItems]);

  const roundCurrency = (value: number) =>
    Math.round((Number(value) || 0) * 100) / 100;

  const cartDiscount = useMemo(() => {
    const normalizedNet = roundCurrency(cartTotals.net);
    const defaultTotals = {
      percent: 0,
      amountNet: 0,
      amountGross: 0,
      netAfter: cartTotals.net,
      grossAfter: cartTotals.gross,
    };

    if (!Number.isFinite(normalizedNet) || totalDiscountTiers.length === 0) {
      return defaultTotals;
    }

    const match = totalDiscountTiers.reduce(
      (best, tier) => {
        const threshold = roundCurrency(tier.threshold);
        if (!Number.isFinite(threshold) || threshold > normalizedNet) {
          return best;
        }
        if (!best || threshold > roundCurrency(best.threshold)) {
          return tier;
        }
        return best;
      },
      null as TotalDiscountTier | null,
    );
    const percent = Number.isFinite(match?.percent) ? match.percent : 0;
    if (!percent) {
      return defaultTotals;
    }

    const amountNet = roundCurrency(normalizedNet * (percent / 100));
    const amountGross = roundCurrency(cartTotals.gross * (percent / 100));

    return {
      percent,
      amountNet,
      amountGross,
      netAfter: roundCurrency(cartTotals.net - amountNet),
      grossAfter: roundCurrency(cartTotals.gross - amountGross),
    };
  }, [cartTotals, totalDiscountTiers]);

  const payableTotals = useMemo(() => {
    const hasDiscount =
      Number.isFinite(cartDiscount.percent) && cartDiscount.percent > 0;

    return {
      net: hasDiscount ? cartDiscount.netAfter : cartTotals.net,
      gross: hasDiscount ? cartDiscount.grossAfter : cartTotals.gross,
    };
  }, [cartDiscount, cartTotals]);

  const productById = useMemo(() => {
    const map = new Map<number, Product>();
    products.forEach((product) => {
      const id = Number(product?.id);
      if (Number.isInteger(id) && id > 0) {
        map.set(id, product);
      }
    });
    return map;
  }, [products]);

  const normalizeRecommendationItems = (
    rows: unknown,
  ): RecommendationItem[] => {
    if (!Array.isArray(rows)) {
      return [];
    }

    const unique = new Map<number, RecommendationItem>();
    rows.forEach((row) => {
      const source =
        row && typeof row === "object" ? (row as Record<string, unknown>) : {};
      const id = Number(source.id ?? source.productId);
      if (!Number.isInteger(id) || id <= 0 || unique.has(id)) {
        return;
      }

      const linkedProduct = productById.get(id);
      if (!linkedProduct) {
        return;
      }
      const name = formatProductName(linkedProduct);
      const description =
        typeof linkedProduct.description === "string"
          ? linkedProduct.description.trim()
          : "";
      const availabilityLabel = formatSalesDaysLabel(linkedProduct.salesDays);
      const price = Number(linkedProduct.price);

      unique.set(id, {
        id,
        name,
        product: linkedProduct,
        description,
        availabilityLabel,
        price: Number.isFinite(price) ? price : null,
        unit: linkedProduct?.unit || null,
      });
    });

    return Array.from(unique.values()).slice(0, maxRecommendations);
  };

  const loadRecommendationsForProduct = async (
    product: Product | null | undefined,
  ) => {
    const productId = Number(product?.id);
    if (!Number.isInteger(productId) || productId <= 0) {
      return;
    }
    const requestPointId = activePointIdRef.current;

    if (
      Object.prototype.hasOwnProperty.call(
        recommendationsCacheRef.current,
        productId,
      )
    ) {
      return;
    }

    if (recommendationsInFlightRef.current.has(productId)) {
      return;
    }

    recommendationsInFlightRef.current.add(productId);
    setRecommendationsLoadingByProduct((prev) => ({
      ...prev,
      [productId]: true,
    }));
    try {
      const rows = await fetchProductRecommendations(productId, {
        limit: maxRecommendations,
      });
      if (requestPointId !== activePointIdRef.current) {
        return;
      }
      const items = normalizeRecommendationItems(rows);

      setRecommendationsByProduct((prev) => {
        if (Object.prototype.hasOwnProperty.call(prev, productId)) {
          return prev;
        }
        return {
          ...prev,
          [productId]: items,
        };
      });
    } catch (err) {
      if (requestPointId !== activePointIdRef.current) {
        return;
      }
      console.warn(
        `Nie udało się pobrać rekomendacji dla produktu ${productId}:`,
        err,
      );
      setRecommendationsByProduct((prev) => {
        if (Object.prototype.hasOwnProperty.call(prev, productId)) {
          return prev;
        }
        return {
          ...prev,
          [productId]: [],
        };
      });
    } finally {
      recommendationsInFlightRef.current.delete(productId);
      setRecommendationsLoadingByProduct((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, productId)) {
          return prev;
        }
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  };

  const visibleProducts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return products.filter((product) => {
      if (product?.isActive === false) {
        return false;
      }
      const productGroupId = product.groupId || "inne";
      const matchesGroup =
        groupFilter === "all" ||
        groupFilter === "" ||
        productGroupId === groupFilter;
      if (!matchesGroup) {
        return false;
      }
      const nameLower = product.name?.toLowerCase() || "";
      const descriptionLower = product.description?.toLowerCase() || "";
      const matchesQuery =
        !normalizedQuery ||
        nameLower.includes(normalizedQuery) ||
        descriptionLower.includes(normalizedQuery);
      if (!matchesQuery) {
        return false;
      }
      if (!activeSalesDay) {
        return true;
      }
      return isProductAvailableOnDay(product, activeSalesDay);
    });
  }, [products, groupFilter, searchQuery, activeSalesDay]);

  const { promotedProducts, regularProducts } = useMemo(() => {
    const withLabels = visibleProducts.map((product) => ({
      ...product,
      salesDaysLabel: formatSalesDaysLabel(product.salesDays),
      displayName: formatProductName(product),
    }));
    const promoted = [];
    const regular = [];
    withLabels.forEach((product) => {
      if (product.isPromoted) {
        promoted.push(product);
      } else {
        regular.push(product);
      }
    });
    promoted.sort((a, b) =>
      a.name.localeCompare(b.name, "pl", { sensitivity: "base" }),
    );
    regular.sort((a, b) =>
      a.name.localeCompare(b.name, "pl", { sensitivity: "base" }),
    );
    return { promotedProducts: promoted, regularProducts: regular };
  }, [visibleProducts]);

  const pageSizeOptions = [10, 20, 50, 100];
  const totalRegularPages = Math.max(
    1,
    Math.ceil(regularProducts.length / itemsPerPage),
  );

  useEffect(() => {
    if (currentPage > totalRegularPages) {
      setCurrentPage(totalRegularPages);
    }
  }, [currentPage, totalRegularPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [groupFilter, searchQuery, activePage, viewMode, itemsPerPage]);

  const paginatedRegularProducts = useMemo(() => {
    const safePage = Math.min(currentPage, totalRegularPages);
    const start = (safePage - 1) * itemsPerPage;
    return regularProducts.slice(start, start + itemsPerPage);
  }, [regularProducts, currentPage, totalRegularPages, itemsPerPage]);

  const renderPagination = () => {
    if (regularProducts.length === 0) {
      return null;
    }

    return (
      <Pagination
        currentPage={currentPage}
        totalPages={totalRegularPages}
        pageSize={itemsPerPage}
        pageSizeOptions={pageSizeOptions}
        onPageChange={setCurrentPage}
        onPageSizeChange={(value) => {
          if (!Number.isFinite(value) || value <= 0) {
            return;
          }
          setItemsPerPage(value);
        }}
      />
    );
  };

  const ordersPageSizeOptions = [10, 20, 50, 100];

  const normalizedOrdersFrom = useMemo(() => {
    if (!ordersDateFrom) {
      return null;
    }
    const parsed = new Date(ordersDateFrom);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }, [ordersDateFrom]);

  const normalizedOrdersTo = useMemo(() => {
    if (!ordersDateTo) {
      return null;
    }
    const parsed = new Date(ordersDateTo);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }, [ordersDateTo]);

  const normalizedOrdersQuery = useMemo(() => {
    return ordersSearchQuery.trim().toLowerCase();
  }, [ordersSearchQuery]);

  const filteredOrders = useMemo(() => {
    if (
      !normalizedOrdersFrom &&
      !normalizedOrdersTo &&
      !normalizedOrdersQuery
    ) {
      return orders;
    }

    return orders.filter((order) => {
      if (!order.placedAt) {
        return false;
      }
      const parsed = new Date(order.placedAt);
      if (Number.isNaN(parsed.getTime())) {
        return false;
      }
      const orderDate = new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate(),
      );

      if (normalizedOrdersFrom && orderDate < normalizedOrdersFrom) {
        return false;
      }
      if (normalizedOrdersTo && orderDate > normalizedOrdersTo) {
        return false;
      }
      if (!normalizedOrdersQuery) {
        return true;
      }

      const searchValue = [
        formatOrderLabel(order),
        getOrderStatusLabel(order),
        formatDateTime(order.placedAt),
        formatDate(order.deliveryDate),
        String(order.itemCount ?? ""),
        String(order.total ?? ""),
        String(order.totalNet ?? ""),
        String(order.httpId ?? ""),
        String(order.internalId ?? ""),
      ]
        .join(" ")
        .toLowerCase();

      return searchValue.includes(normalizedOrdersQuery);
    });
  }, [orders, normalizedOrdersFrom, normalizedOrdersTo, normalizedOrdersQuery]);

  const totalOrdersPages = Math.max(
    1,
    Math.ceil(filteredOrders.length / ordersPageSize),
  );

  useEffect(() => {
    if (ordersPage > totalOrdersPages) {
      setOrdersPage(totalOrdersPages);
    }
  }, [ordersPage, totalOrdersPages]);

  useEffect(() => {
    setOrdersPage(1);
  }, [ordersDateFrom, ordersDateTo, ordersPageSize, ordersSearchQuery]);

  const paginatedOrders = useMemo(() => {
    const safePage = Math.min(ordersPage, totalOrdersPages);
    const start = (safePage - 1) * ordersPageSize;
    return filteredOrders.slice(start, start + ordersPageSize);
  }, [filteredOrders, ordersPage, totalOrdersPages, ordersPageSize]);

  const settlementsPageSizeOptions = [10, 20, 50, 100];

  const normalizedSettlementsFrom = useMemo(() => {
    if (!settlementsDateFrom) {
      return null;
    }
    const parsed = new Date(settlementsDateFrom);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }, [settlementsDateFrom]);

  const normalizedSettlementsTo = useMemo(() => {
    if (!settlementsDateTo) {
      return null;
    }
    const parsed = new Date(settlementsDateTo);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }, [settlementsDateTo]);

  const normalizedSettlementsQuery = useMemo(() => {
    return settlementsSearchQuery.trim().toLowerCase();
  }, [settlementsSearchQuery]);

  const filteredSettlements = useMemo(() => {
    if (
      !normalizedSettlementsFrom &&
      !normalizedSettlementsTo &&
      !normalizedSettlementsQuery
    ) {
      return settlements;
    }

    return settlements.filter((invoice) => {
      if (!invoice.issuedAt) {
        return false;
      }
      const parsed = new Date(invoice.issuedAt);
      if (Number.isNaN(parsed.getTime())) {
        return false;
      }
      const invoiceDate = new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate(),
      );

      if (
        normalizedSettlementsFrom &&
        invoiceDate < normalizedSettlementsFrom
      ) {
        return false;
      }
      if (normalizedSettlementsTo && invoiceDate > normalizedSettlementsTo) {
        return false;
      }
      if (!normalizedSettlementsQuery) {
        return true;
      }

      const searchValue = [
        String(invoice.invoiceNumber ?? ""),
        formatDate(invoice.issuedAt),
        formatDate(invoice.dueDate),
        formatCurrency(invoice.grossAmount),
        formatCurrency(invoice.amountDue),
        String(invoice.paymentTermsCode ?? ""),
        String(invoice.overdueDays ?? ""),
      ]
        .join(" ")
        .toLowerCase();

      return searchValue.includes(normalizedSettlementsQuery);
    });
  }, [
    settlements,
    normalizedSettlementsFrom,
    normalizedSettlementsTo,
    normalizedSettlementsQuery,
  ]);

  const totalSettlementsPages = Math.max(
    1,
    Math.ceil(filteredSettlements.length / settlementsPageSize),
  );

  useEffect(() => {
    if (settlementsPage > totalSettlementsPages) {
      setSettlementsPage(totalSettlementsPages);
    }
  }, [settlementsPage, totalSettlementsPages]);

  useEffect(() => {
    setSettlementsPage(1);
  }, [
    settlementsDateFrom,
    settlementsDateTo,
    settlementsPageSize,
    settlementsSearchQuery,
  ]);

  const paginatedSettlements = useMemo(() => {
    const safePage = Math.min(settlementsPage, totalSettlementsPages);
    const start = (safePage - 1) * settlementsPageSize;
    return filteredSettlements.slice(start, start + settlementsPageSize);
  }, [
    filteredSettlements,
    settlementsPage,
    totalSettlementsPages,
    settlementsPageSize,
  ]);

  const renderOrdersPagination = () => {
    if (filteredOrders.length === 0) {
      return null;
    }

    return (
      <Pagination
        currentPage={ordersPage}
        totalPages={totalOrdersPages}
        pageSize={ordersPageSize}
        pageSizeOptions={ordersPageSizeOptions}
        onPageChange={setOrdersPage}
        onPageSizeChange={(value) => {
          if (!Number.isFinite(value) || value <= 0) {
            return;
          }
          setOrdersPageSize(value);
        }}
      />
    );
  };

  const renderSettlementsPagination = () => {
    if (filteredSettlements.length === 0) {
      return null;
    }

    return (
      <Pagination
        currentPage={settlementsPage}
        totalPages={totalSettlementsPages}
        pageSize={settlementsPageSize}
        pageSizeOptions={settlementsPageSizeOptions}
        onPageChange={setSettlementsPage}
        onPageSizeChange={(value) => {
          if (!Number.isFinite(value) || value <= 0) {
            return;
          }
          setSettlementsPageSize(value);
        }}
      />
    );
  };

  const handleDeliveryDateChange = (nextValue: string) => {
    const trimmed = typeof nextValue === "string" ? nextValue.trim() : "";
    const normalized = trimmed || minDeliveryDate || "";
    if (!normalized) {
      setDeliveryDate("");
      return;
    }

    const parsed = new Date(normalized);
    const dayOfWeek = parsed.getDay();

    // Blokada niedzieli
    if (!Number.isNaN(parsed.getTime()) && dayOfWeek === 0) {
      setStatus({
        type: "error",
        message: "Dostawy w niedzielę są niedostępne. Wybierz inny dzień.",
      });
      setCartVisible(true);
      return;
    }

    // Blokada piątku
    if (!Number.isNaN(parsed.getTime()) && dayOfWeek === 5) {
      setStatus({
        type: "error",
        message: "Dostawy w piątek są niedostępne. Wybierz inny dzień.",
      });
      setCartVisible(true);
      return;
    }

    // Czyszczenie błędów związanych z dniami tygodnia
    if (
      status.type === "error" &&
      (status.message?.includes("niedziel") ||
        status.message?.includes("piątek"))
    ) {
      setStatus({ type: "", message: "" });
    }

    if (minDeliveryDate && normalized < minDeliveryDate) {
      setDeliveryDate(minDeliveryDate);
      return;
    }

    setDeliveryDate(normalized);
    setHasExistingOrder(false);
    setExistingOrderMessage("");

    const targetDay = getSalesDayFromDate(normalized);
    if (targetDay) {
      const unavailableCount = Object.values(cart).reduce((count, item) => {
        return count + (isProductAvailableOnDay(item, targetDay) ? 0 : 1);
      }, 0);
      if (unavailableCount > 0) {
        setCartVisible(true);
        setStatus({
          type: "error",
          message: `Koszyk zawiera ${unavailableCount} pozycji niedostępnych na wybraną datę dostawy.`,
        });
      } else if (
        status.type === "error" &&
        status.message?.includes("Koszyk zawiera")
      ) {
        setStatus({ type: "", message: "" });
      }
    }
  };

  const marketingEntries = useMemo(() => {
    return Array.isArray(marketing) ? marketing : [];
  }, [marketing]);

  const staticContact = {
    name: "Agnieszka Nowak",
    phone: "+48 500 123 456",
    email: "b2b@kropek.pl",
    availability: "Pon - Pt 7:00-15:00",
  };

  const staticNewsletter = {
    description:
      "Zapisz się na comiesięczny newsletter z poradami sprzedażowymi i sezonowymi forecastami.",
    cta: "Zapisz się",
  };

  const filters = (
    <div className="catalog-filters">
      <div className="filter-control">
        <label htmlFor="group-filter">Grupa towarów</label>
        <select
          id="group-filter"
          value={groupFilter}
          onChange={(event) => setGroupFilter(event.target.value)}
        >
          <option value="all">Wszystkie grupy</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
      </div>
      <div className="search-control">
        <label htmlFor="product-search">Wyszukaj</label>
        <input
          id="product-search"
          type="search"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="np. bagietka"
        />
      </div>
      <div className="filter-control">
        <label htmlFor="delivery-date-filter">Data dostawy</label>
        <input
          id="delivery-date-filter"
          type="date"
          value={deliveryDate || ""}
          min={minDeliveryDate}
          onChange={(event) => handleDeliveryDateChange(event.target.value)}
        />
      </div>
    </div>
  );

  const renderViewToggle = () => (
    <div
      className="view-toggle"
      role="group"
      aria-label="Zmiana widoku produktów"
    >
      <button
        type="button"
        className={viewMode === "grid" ? "active" : ""}
        onClick={() => setViewMode("grid")}
      >
        Widok kafelkowy
      </button>
      <button
        type="button"
        className={viewMode === "list" ? "active" : ""}
        onClick={() => setViewMode("list")}
      >
        Widok listy
      </button>
    </div>
  );

  const updateQuantity = (
    product: Product,
    quantity: number,
    options: QuantityChangeOptions = {},
  ) => {
    const source = options?.source || "catalog";
    const minQuantity = resolveMinQuantity(product);
    const step = resolveStep(product);
    const effectiveStep = step > 0 ? step : 1;
    const normalized = (() => {
      const numeric = Number(quantity);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        return 0;
      }
      const rounded = Math.ceil(numeric / effectiveStep) * effectiveStep;
      return Math.max(minQuantity, rounded);
    })();

    if (normalized > 0 && source !== "recommendation") {
      void loadRecommendationsForProduct(product);
      setLastModifiedProductId(product.id);
    }

    setCart((prev) => {
      if (normalized <= 0) {
        const copy = { ...prev };
        delete copy[product.id];
        return copy;
      }
      return {
        ...prev,
        [product.id]: { ...product, quantity: normalized },
      };
    });
  };

  const removeCartItem = (productId: number) => {
    setCart((prev) => {
      if (!prev[productId]) {
        return prev;
      }
      const copy = { ...prev };
      delete copy[productId];
      return copy;
    });
  };

  const handleCartQuantityChange = (productId: number, quantity: number) => {
    const cartItem = cart[productId];
    if (!cartItem) {
      return;
    }
    updateQuantity(cartItem, quantity);
  };

  const getOrderDetailItemKey = (item: OrderDetailLine, index: number) => {
    const rowId = item.id != null ? String(item.id) : `line-${index}`;
    const productId =
      item.productId != null ? String(item.productId) : "unknown-product";
    return `${rowId}:${productId}:${index}`;
  };

  const getOrderDetailQuantityValue = (
    item: OrderDetailLine,
    itemKey: string,
  ) => {
    const fromState = Number(orderDetailQuantities[itemKey]);
    if (Number.isFinite(fromState) && fromState > 0) {
      return fromState;
    }
    const original = Number(item.quantity);
    return Number.isFinite(original) && original > 0 ? original : 0;
  };

  const buildOrderDetailQuantities = (items?: OrderDetailLine[]) => {
    const next: Record<string, number> = {};
    if (!Array.isArray(items)) {
      return next;
    }
    items.forEach((item, index) => {
      const itemKey = getOrderDetailItemKey(item, index);
      const quantity = Number(item.quantity);
      next[itemKey] = Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
    });
    return next;
  };

  const resolveOrderDetailId = (
    order: OrderListItem | OrderDetails | null | undefined,
  ): Id | null => {
    if (!order) {
      return null;
    }
    return order.internalId ?? order.id ?? null;
  };

  const handleShowOrderDetails = async (
    order: OrderListItem | OrderDetails,
  ) => {
    const detailId = resolveOrderDetailId(order);
    if (!detailId) {
      setOrderDetailsError("Nieprawidłowy identyfikator zamówienia.");
      setSelectedOrderId(null);
      setSelectedOrder(null);
      return;
    }

    setSelectedOrderId(detailId);
    setOrderDetailsError("");
    setSelectedOrder(null);
    setOrderDetailsLoading(true);

    if (order?.source === "local" && Array.isArray(order.items)) {
      setSelectedOrder(order);
      setOrderDetailQuantities(buildOrderDetailQuantities(order.items));
      setOrderDetailsLoading(false);
      return;
    }
    try {
      const details = (await fetchOrderDetails(detailId)) as OrderDetails;
      setSelectedOrder(details);
      setOrderDetailQuantities(buildOrderDetailQuantities(details.items));
    } catch (err) {
      setOrderDetailsError(err.message);
      setSelectedOrder(null);
      setOrderDetailQuantities({});
    } finally {
      setOrderDetailsLoading(false);
    }
  };

  const handleCloseOrderDetails = () => {
    setSelectedOrderId(null);
    setSelectedOrder(null);
    setOrderDetailsError("");
    setOrderDetailsLoading(false);
    setOrderDetailQuantities({});
  };

  const handleOrderDetailQuantityChange = (
    itemKey: string,
    quantity: number,
  ) => {
    const normalized = Number(quantity);
    setOrderDetailQuantities((prev) => ({
      ...prev,
      [itemKey]: Number.isFinite(normalized) && normalized > 0 ? normalized : 0,
    }));
  };

  const handleAddSingleProductFromOrder = (
    item: OrderDetailLine,
    index: number,
  ) => {
    const productId = Number(item.productId);
    const productName = item.name || "produkt";
    if (!Number.isInteger(productId) || productId <= 0) {
      setStatus({
        type: "error",
        message: `Produkt "${productName}" ma nieprawidlowy identyfikator.`,
      });
      return;
    }

    const product = productById.get(productId);
    if (!product) {
      setStatus({
        type: "error",
        message: `Produkt "${productName}" nie jest juz dostepny.`,
      });
      return;
    }

    const itemKey = getOrderDetailItemKey(item, index);
    const quantityToAdd = getOrderDetailQuantityValue(item, itemKey);
    if (quantityToAdd <= 0) {
      setStatus({
        type: "error",
        message: "Ilosc musi byc wieksza od 0.",
      });
      return;
    }

    const currentQuantity = cart[productId]?.quantity || 0;
    updateQuantity(product, currentQuantity + quantityToAdd, {
      source: "order-copy",
    });

    setStatus({
      type: "success",
      message: `Dodano ${quantityToAdd} x ${productName} do koszyka.`,
    });
  };

  const handleCopyOrderToCart = () => {
    if (
      !selectedOrder ||
      !selectedOrder.items ||
      selectedOrder.items.length === 0
    ) {
      setStatus({ type: "error", message: "Brak produktow do skopiowania." });
      return;
    }

    let addedCount = 0;
    let unavailableCount = 0;
    let skippedCount = 0;

    selectedOrder.items.forEach((item, index) => {
      const productId = Number(item.productId);
      if (!Number.isInteger(productId) || productId <= 0) {
        skippedCount++;
        return;
      }

      const itemKey = getOrderDetailItemKey(item, index);
      const quantityToAdd = getOrderDetailQuantityValue(item, itemKey);
      if (quantityToAdd <= 0) {
        skippedCount++;
        return;
      }

      const product = productById.get(productId);
      if (!product) {
        unavailableCount++;
        return;
      }

      const currentQuantity = cart[productId]?.quantity || 0;
      updateQuantity(product, currentQuantity + quantityToAdd, {
        source: "order-copy",
      });
      addedCount++;
    });

    handleCloseOrderDetails();
    setActivePage("catalog");

    if (addedCount > 0) {
      const details: string[] = [];
      if (unavailableCount > 0) {
        details.push(unavailableCount + " pozycji nie jest juz dostepnych.");
      }
      if (skippedCount > 0) {
        details.push(
          skippedCount + " pozycji pominieto (ilosc 0 lub nieprawidlowa).",
        );
      }
      const suffix = details.length ? " " + details.join(" ") : "";
      const message = "Dodano " + addedCount + " pozycji do koszyka." + suffix;
      setStatus({ type: "success", message });
    } else {
      const reason =
        unavailableCount > 0
          ? "Produkty z podgladu nie sa juz dostepne."
          : "Ustaw ilosc wieksza od 0 i sprobuj ponownie.";
      setStatus({
        type: "error",
        message: "Nie dodano produktow do koszyka. " + reason,
      });
    }
  };

  const handleOrderSubmit = async () => {
    setStatus({ type: "", message: "" });
    if (cartItems.length === 0) {
      setStatus({ type: "error", message: "Dodaj produkty do koszyka." });
      return;
    }

    const tooSmallLines = cartItems.filter((item) => {
      const minQty = resolveMinQuantity(item);
      return item.quantity > 0 && item.quantity < minQty;
    });
    if (tooSmallLines.length > 0) {
      const minQty = tooSmallLines.reduce(
        (max, item) => Math.max(max, resolveMinQuantity(item)),
        0,
      );
      setStatus({
        type: "error",
        message: `Minimalna ilość na pozycję to ${minQty} szt.`,
      });
      setCartVisible(true);
      return;
    }

    if (!deliveryDate) {
      setStatus({
        type: "error",
        message: "Wybierz datę dostawy przed złożeniem zamówienia.",
      });
      setCartVisible(true);
      return;
    }

    const invalidMultipliers = cartItems.filter((item) => {
      const step = resolveStep(item);
      if (!Number.isFinite(step) || step <= 0) {
        return false;
      }
      return item.quantity % step !== 0;
    });
    if (invalidMultipliers.length > 0) {
      const stepLabel = invalidMultipliers
        .map((item) => resolveStep(item))
        .filter((value) => Number.isFinite(value) && value > 0)
        .sort((a, b) => b - a)[0];
      setStatus({
        type: "error",
        message: `Ilość musi być wielokrotnością pakowania (${stepLabel}).`,
      });
      setCartVisible(true);
      return;
    }

    if (minDeliveryDate && deliveryDate < minDeliveryDate) {
      setStatus({
        type: "error",
        message: `Najwcześniejsza dostępna dostawa to ${formatDate(minDeliveryDate) || minDeliveryDate}.`,
      });
      setDeliveryDate(minDeliveryDate);
      setCartVisible(true);
      return;
    }

    if (cartTotals.net < minOrderNet) {
      setStatus({
        type: "error",
        message: `Minimalna wartość logistyczna to ${minOrderNet.toFixed(0)} zł netto.`,
      });
      setCartVisible(true);
      return;
    }

    if (activeSalesDay) {
      const unavailable = cartItems.filter((item) => !item.isAvailable);
      if (unavailable.length > 0) {
        setStatus({
          type: "error",
          message:
            "Koszyk zawiera produkty niedostępne na wybraną datę dostawy.",
        });
        setCartVisible(true);
        return;
      }
    }

    if (hasExistingOrder) {
      const message =
        existingOrderMessage ||
        "Zamówienie dla wybranego punktu na tę datę zostało już złożone.";
      setStatus({ type: "error", message });
      setCartVisible(true);
      return;
    }

    const confirmationNet = payableTotals.net.toFixed(2);
    const confirmationGross = payableTotals.gross.toFixed(2);
    const pointLabel = user.pointName || "wybranego punktu";
    const deliveryLabel = formatDate(deliveryDate) || deliveryDate;

    setConfirmModalData({
      pointLabel,
      deliveryLabel,
      netAmount: confirmationNet,
      grossAmount: confirmationGross,
    });
    setShowConfirmModal(true);
  };

  const handleConfirmOrder = async () => {
    setShowConfirmModal(false);

    if (!confirmModalData) {
      return;
    }

    const { deliveryLabel } = confirmModalData;

    try {
      const isMobile =
        typeof window !== "undefined" && window.innerWidth <= 1040;

      const order = await submitOrder({
        items: cartItems.map(
          ({ lineNet, lineGross, salesDaysLabel, ...item }) => item,
        ),
        notes,
        deliveryDate,
        totalDiscount: cartDiscount.percent,
      });

      if (isMobile) {
        setCartVisible(false);
      }

      setStatus({
        type: "success",
        message: `Zamówienie ${order.id} przyjęte. Dostawa: ${formatDate(order.deliveryDate) || deliveryLabel}. Łączna wartość: netto ${(Number.isFinite(order.totalNet) ? order.totalNet : payableTotals.net).toFixed(2)} zł, brutto ${(Number.isFinite(order.total) ? order.total : payableTotals.gross).toFixed(2)} zł.`,
        actionLabel: "Wyświetl",
        onAction: async () => {
          await handleShowOrderDetails(order);
        },
      });
      setCart({});
      setNotes("");
      setHasExistingOrder(true);
      setExistingOrderMessage(
        formatExistingOrderMessage(order.deliveryDate || deliveryDate),
      );

      try {
        const refreshedOrders = await fetchOrders();
        setOrders(refreshedOrders);
      } catch (refreshErr) {
        console.error("Orders refresh error:", refreshErr);
      }

      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      setStatus({ type: "error", message: err.message });
      setCartVisible(true);
    } finally {
      setConfirmModalData(null);
    }
  };

  const handleCancelOrder = () => {
    setShowConfirmModal(false);
    setConfirmModalData(null);
  };

  const handleCartTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (event.touches?.length) {
      const y = event.touches[0].clientY;
      touchStartYRef.current = y;
      touchLastYRef.current = y;
    }
  };

  const handleCartTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (event.touches?.length) {
      touchLastYRef.current = event.touches[0].clientY;
    }
  };

  const handleCartTouchEnd = () => {
    const startY = touchStartYRef.current;
    const lastY = touchLastYRef.current;
    touchStartYRef.current = null;
    touchLastYRef.current = null;
    if (startY === null || lastY === null) {
      return;
    }
    const delta = lastY - startY;
    const threshold = 60;
    if (delta > threshold) {
      setCartVisible(false);
    } else if (delta < -threshold) {
      setCartVisible(true);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      onLogout();
    }
  };

  const renderNavigation = () => {
    const items: Array<{ id: OrdersActivePage; label: string }> = [
      { id: "home", label: "Start" },
      { id: "catalog", label: "Produkty" },
      { id: "my-orders", label: "Moje zamówienia" },
      { id: "settlements", label: "Rozrachunki" },
      { id: "profile", label: "Profil" },
    ];
    const navClassName = `main-nav${isNavOpen ? " open" : ""}`;
    return (
      <nav id="main-nav" className={navClassName} aria-label="Nawigacja główna">
        <div className="nav-actions">{renderPointSelector("mobile")}</div>
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className={activePage === item.id ? "active" : ""}
            onClick={() => {
              setActivePage(item.id);
              setNavOpen(false);
            }}
            aria-pressed={activePage === item.id}
          >
            {item.label}
          </button>
        ))}
        <div className="nav-actions nav-actions--bottom">
          <button onClick={handleLogout} className="secondary">
            Wyloguj
          </button>
        </div>
      </nav>
    );
  };

  const renderPointSelector = (idSuffix = "") => {
    if (points.length === 0) {
      return null;
    }

    const value = user.pointId == null ? "" : String(user.pointId);
    const inputId = idSuffix
      ? `point-context-select-${idSuffix}`
      : "point-context-select";

    return (
      <div className="point-selector-wrapper" aria-busy={pointChangeLoading}>
        <label htmlFor={inputId} className="point-selector-label">
          Punkt:
        </label>
        <select
          id={inputId}
          className="point-selector"
          value={value}
          onChange={handlePointSwitch}
          disabled={pointChangeLoading || points.length <= 1}
          title="Wybierz punkt handlowy"
        >
          {points.map((point) => (
            <option key={point.id} value={point.id}>
              {point.name}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderOrderDetailContent = () => {
    if (orderDetailsLoading) {
      return null;
    }
    if (orderDetailsError) {
      return <div className="alert">{orderDetailsError}</div>;
    }
    if (!selectedOrder) {
      return (
        <p className="order-notes">
          Nie udało się pobrać szczegółów zamówienia.
        </p>
      );
    }

    const detail = selectedOrder;
    return (
      <article className="order-details-card">
        <header>
          <div>
            <h3>{formatOrderLabel(detail)}</h3>
            <p>Złożono: {formatDateTime(detail.placedAt)}</p>
            <p>Dostawa: {formatDate(detail.deliveryDate) || "—"}</p>
          </div>
          <div className="orders-table-total">
            Razem: <strong>{formatCurrency(detail.total)}</strong>
          </div>
        </header>
        <p>Status: {getOrderStatusLabel(detail)}</p>
        {detail.totalNet != null && (
          <p>Kwota netto: {formatCurrency(detail.totalNet)}</p>
        )}
        {detail.total != null && (
          <p>Kwota brutto: {formatCurrency(detail.total)}</p>
        )}
        {detail.notes && <p className="order-notes">Notatki: {detail.notes}</p>}
        <ul>
          {detail.items?.map((item, index) => {
            const itemKey = getOrderDetailItemKey(item, index);
            const lineValue =
              item.lineTotal ??
              (item.price != null && item.quantity != null
                ? Number(item.price) * Number(item.quantity)
                : item.price);
            const productId = Number(item.productId);
            const linkedProduct =
              Number.isInteger(productId) && productId > 0
                ? productById.get(productId)
                : undefined;
            const quantityValue = getOrderDetailQuantityValue(item, itemKey);
            const step = linkedProduct ? resolveStep(linkedProduct) : 1;
            const minQuantity = linkedProduct
              ? resolveMinQuantity(linkedProduct)
              : 1;
            const unitLabel = item.unit || linkedProduct?.unit || "szt";
            const canAddProduct = Boolean(linkedProduct);

            return (
              <li
                key={
                  item.id ||
                  String(item.productId) + "-" + item.name + "-" + index
                }
              >
                <div className="order-detail-item-main">
                  <strong>{item.name}</strong>
                  <span>
                    Oryginalnie: {item.quantity} {unitLabel}
                  </span>
                  {!canAddProduct && (
                    <span className="order-detail-item-warning">
                      Produkt nie jest już dostępnny.
                    </span>
                  )}
                </div>
                <span className="order-detail-line-total">
                  {formatCurrency(lineValue)}
                </span>
                <div className="order-detail-item-controls">
                  <QuantityInput
                    value={quantityValue}
                    onChange={(nextValue) =>
                      handleOrderDetailQuantityChange(itemKey, nextValue)
                    }
                    step={step}
                    minQuantity={minQuantity}
                  />
                  <button
                    type="button"
                    className="secondary order-detail-add-button"
                    onClick={() => handleAddSingleProductFromOrder(item, index)}
                    disabled={!canAddProduct}
                  >
                    Dodaj
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        {(!detail.items || detail.items.length === 0) && (
          <p className="order-notes">Brak pozycji do wyświetlenia.</p>
        )}
        <footer className="order-details-footer">
          <button
            type="button"
            className="primary"
            onClick={handleCopyOrderToCart}
            disabled={!detail.items || detail.items.length === 0}
          >
            Dodaj do koszyka
          </button>
          <button
            type="button"
            className="secondary"
            onClick={handleCloseOrderDetails}
          >
            Zamknij podgląd
          </button>
        </footer>
      </article>
    );
  };

  return (
    <div className="app-shell sticky-shell">
      <header className="top-bar" ref={topBarRef}>
        <div className="top-bar-left">
          <CompanyLogo />
          <div>
            <h1>Panel zamówień Kropek</h1>
            <p>Witaj, {user.displayName || user.username}</p>
          </div>
        </div>
        <div className="top-bar-actions">
          <div className="top-bar-actions-row">
            {renderPointSelector()}
            <button
              type="button"
              className={`nav-toggle${isNavOpen ? " open" : ""}`}
              onClick={() => setNavOpen((prev) => !prev)}
              aria-expanded={isNavOpen}
              aria-controls="main-nav"
              aria-label={isNavOpen ? "Zamknij menu" : "Otwórz menu"}
            >
              <span className="nav-toggle-icon" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
            <button onClick={handleLogout} className="secondary">
              Wyloguj
            </button>
          </div>
          {renderNavigation()}
        </div>
      </header>

      {selectedOrderId && (selectedOrder || orderDetailsError) && (
        <div
          className="order-details-overlay visible"
          onClick={handleCloseOrderDetails}
          role="presentation"
        >
          <div
            className="order-details-modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            {renderOrderDetailContent()}
          </div>
        </div>
      )}

      {showConfirmModal &&
        confirmModalData &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="order-confirm-overlay visible"
            onClick={handleCancelOrder}
            role="presentation"
          >
            <div
              className="order-confirm-modal"
              role="dialog"
              aria-modal="true"
              onClick={(event) => event.stopPropagation()}
            >
              <h2>Potwierdzenie zamówienia</h2>
              <div className="confirm-content">
                <p>
                  <strong>Punkt dostawy:</strong> {confirmModalData.pointLabel}
                </p>
                <p>
                  <strong>Data dostawy:</strong>{" "}
                  {confirmModalData.deliveryLabel}
                </p>
                <p>
                  <strong>Wartość netto:</strong> {confirmModalData.netAmount}{" "}
                  zł
                </p>
                <p>
                  <strong>Wartość brutto:</strong>{" "}
                  {confirmModalData.grossAmount} zł
                </p>
              </div>
              <p className="confirm-question">
                Czy na pewno chcesz złożyć to zamówienie?
              </p>
              <div className="confirm-actions">
                <button
                  type="button"
                  className="primary"
                  onClick={handleConfirmOrder}
                >
                  Potwierdź zamówienie
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={handleCancelOrder}
                >
                  Anuluj
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {status.message && (
        <div
          className={`banner banner-sticky ${status.type || "info"}`}
          role={status.type === "error" ? "alert" : "status"}
          aria-live={status.type === "error" ? "assertive" : "polite"}
        >
          <span className="banner-message">{status.message}</span>
          <div className="banner-actions">
            {status.actionLabel && status.onAction && (
              <button
                type="button"
                className="banner-action"
                onClick={async () => {
                  setStatus({ type: "", message: "" });
                  await status.onAction?.();
                }}
              >
                {status.actionLabel}
              </button>
            )}
            <button
              type="button"
              className="banner-close"
              onClick={() => setStatus({ type: "", message: "" })}
              aria-label="Zamknij komunikat"
              title="Zamknij komunikat"
            >
              <IconX className="banner-close-icon" />
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loader">Ładowanie danych...</div>
      ) : activePage === "home" ? (
        <section className="content-section landing">
          <SectionHeader
            title="Aktualności Kropek"
            description="Najświeższe informacje dla partnerów B2B – promocje, nowości produktowe i logistyka."
          />
          {marketing ? (
            <>
              {marketingEntries.length === 0 ? (
                <div className="landing-grid">
                  <article className="landing-card">
                    <h3>Brak komunikatów</h3>
                    <p>Aktualnie nie ma opublikowanych ogłoszeń.</p>
                  </article>
                </div>
              ) : (
                <div className="landing-grid">
                  {marketingEntries.map((entry, index) => {
                    const from = formatDate(entry.publishedFrom);
                    const to = formatDate(entry.publishedTo);
                    let rangeText = null;
                    if (from && to) {
                      rangeText = `Publikacja: ${from} – ${to}`;
                    } else if (from) {
                      rangeText = `Publikacja od ${from}`;
                    } else if (to) {
                      rangeText = `Publikacja do ${to}`;
                    }
                    return (
                      <article
                        key={entry.id ?? index}
                        className={`landing-card${index === 0 ? " highlight" : ""}`}
                      >
                        <h3>{entry.title}</h3>
                        {entry.description && <p>{entry.description}</p>}
                        {rangeText && <p className="meta">{rangeText}</p>}
                      </article>
                    );
                  })}
                </div>
              )}
              <aside className="landing-side">
                <div className="landing-card contact">
                  <h3>Kontakt z opiekunem</h3>
                  <p>
                    {staticContact.name} - tel. {staticContact.phone} -{" "}
                    {staticContact.email}
                  </p>
                  <p>Godziny dostępności: {staticContact.availability}</p>
                </div>
                {/* <div className="landing-card newsletter">
                  <h3>Biuletyn</h3>
                  <p>{staticNewsletter.description}</p>
                  <button type="button" className="primary ghost">
                    {staticNewsletter.cta}
                  </button>
                </div> */}
              </aside>
            </>
          ) : (
            <>
              <div className="landing-grid">
                <article className="landing-card">
                  <h3>Brak komunikatów</h3>
                  <p>
                    Nie udało się załadować danych marketingowych. Spróbuj
                    ponownie później.
                  </p>
                </article>
              </div>
              <aside className="landing-side">
                <div className="landing-card contact">
                  <h3>Kontakt z opiekunem</h3>
                  <p>
                    {staticContact.name} - tel. {staticContact.phone} -{" "}
                    {staticContact.email}
                  </p>
                  <p>Godziny dostępności: {staticContact.availability}</p>
                </div>
                <div className="landing-card newsletter">
                  <h3>Biuletyn</h3>
                  <p>{staticNewsletter.description}</p>
                  <button type="button" className="primary ghost">
                    {staticNewsletter.cta}
                  </button>
                </div>
              </aside>
            </>
          )}
        </section>
      ) : activePage === "catalog" ? (
        <>
          <div className="layout">
            <div className="catalog-column">
              {viewMode === "grid" && (
                <ProductGrid
                  promotedProducts={promotedProducts}
                  regularProducts={paginatedRegularProducts}
                  cart={cart}
                  recommendationsByProduct={recommendationsByProduct}
                  recommendationsLoadingByProduct={
                    recommendationsLoadingByProduct
                  }
                  onQuantityChange={updateQuantity}
                  headerActions={renderViewToggle()}
                  filters={filters}
                  pagination={renderPagination()}
                  baseMinQuantity={minItemQuantity}
                  lastModifiedProductId={lastModifiedProductId}
                />
              )}
              {viewMode === "list" && (
                <ProductList
                  promotedProducts={promotedProducts}
                  regularProducts={paginatedRegularProducts}
                  cart={cart}
                  recommendationsByProduct={recommendationsByProduct}
                  recommendationsLoadingByProduct={
                    recommendationsLoadingByProduct
                  }
                  onQuantityChange={updateQuantity}
                  headerActions={renderViewToggle()}
                  filters={filters}
                  pagination={renderPagination()}
                  baseMinQuantity={minItemQuantity}
                  lastModifiedProductId={lastModifiedProductId}
                />
              )}
            </div>
            <CartSummary
              items={cartItems}
              totalNet={cartTotals.net}
              totalGross={cartTotals.gross}
              totalDiscount={cartDiscount.amountNet}
              totalDiscountPercent={cartDiscount.percent}
              totalNetAfterDiscount={cartDiscount.netAfter}
              totalGrossAfterDiscount={cartDiscount.grossAfter}
              notes={notes}
              onNotesChange={setNotes}
              onSubmit={handleOrderSubmit}
              onRemoveItem={removeCartItem}
              onQuantityChange={handleCartQuantityChange}
              deliveryDate={deliveryDate}
              onDeliveryDateChange={handleDeliveryDateChange}
              minDeliveryDate={minDeliveryDate}
              existingOrderMessage={existingOrderMessage}
              hasExistingOrder={hasExistingOrder}
              status={status}
            />
          </div>
          <section className="orders-section">
            <SectionHeader title="Ostatnie zamówienia" />
            {orders.length === 0 ? (
              <p>Brak historii zamówień.</p>
            ) : (
              <>
                <OrdersGrid
                  orders={orders.slice(0, 12)}
                  selectedOrderId={selectedOrderId}
                  orderDetailsLoading={orderDetailsLoading}
                  className="orders-grid"
                  onShowDetails={handleShowOrderDetails}
                  resolveOrderDetailId={resolveOrderDetailId}
                  getOrderKey={getOrderKey}
                  formatOrderLabel={formatOrderLabel}
                  formatDateTime={formatDateTime}
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}
                  getOrderStatusLabel={getOrderStatusLabel}
                />
              </>
            )}
          </section>
        </>
      ) : activePage === "my-orders" ? (
        <section className="content-section">
          <SectionHeader
            title="Moje zamówienia"
            description="Podgląd ostatnich wysłanych zamówień wraz ze statusem i szczegółami pozycji."
          />
          {orders.length === 0 ? (
            <p>Nie złożono jeszcze żadnych zamówień.</p>
          ) : (
            <>
              <RangeSearchFilters
                fromLabel="Data złożenia od"
                toLabel="do"
                fromValue={ordersDateFrom}
                toValue={ordersDateTo}
                onFromChange={setOrdersDateFrom}
                onToChange={setOrdersDateTo}
                searchLabel="Szukaj"
                searchPlaceholder="Numer, status, kwota..."
                searchValue={ordersSearchQuery}
                onSearchChange={setOrdersSearchQuery}
                onClear={() => {
                  setOrdersDateFrom("");
                  setOrdersDateTo("");
                  setOrdersSearchQuery("");
                }}
                isClearDisabled={
                  !ordersDateFrom && !ordersDateTo && !ordersSearchQuery
                }
              />
              {filteredOrders.length === 0 ? (
                <p>Brak zamówień w wybranym zakresie dat.</p>
              ) : (
                <>
                  <OrdersListSection
                    orders={paginatedOrders}
                    selectedOrderId={selectedOrderId}
                    orderDetailsLoading={orderDetailsLoading}
                    onShowDetails={handleShowOrderDetails}
                    resolveOrderDetailId={resolveOrderDetailId}
                    getOrderKey={getOrderKey}
                    formatOrderLabel={formatOrderLabel}
                    formatDateTime={formatDateTime}
                    formatDate={formatDate}
                    formatCurrency={formatCurrency}
                    getOrderStatusLabel={getOrderStatusLabel}
                    pagination={renderOrdersPagination()}
                  />
                </>
              )}
            </>
          )}
        </section>
      ) : activePage === "settlements" ? (
        <section className="content-section settlements">
          <SectionHeader
            title="Rozrachunki"
            description="Lista otwartych faktur powiązanych z aktywnym punktem handlowym."
          />
          {settlements.length === 0 ? (
            <p className="settlements-empty">
              Brak otwartych rozrachunków dla wybranego punktu.
            </p>
          ) : (
            <>
              <RangeSearchFilters
                fromLabel="Data wystawienia od"
                toLabel="do"
                fromValue={settlementsDateFrom}
                toValue={settlementsDateTo}
                onFromChange={setSettlementsDateFrom}
                onToChange={setSettlementsDateTo}
                searchLabel="Szukaj"
                searchPlaceholder="Numer, kwota, termin..."
                searchValue={settlementsSearchQuery}
                onSearchChange={setSettlementsSearchQuery}
                onClear={() => {
                  setSettlementsDateFrom("");
                  setSettlementsDateTo("");
                  setSettlementsSearchQuery("");
                }}
                isClearDisabled={
                  !settlementsDateFrom &&
                  !settlementsDateTo &&
                  !settlementsSearchQuery
                }
              />
              {filteredSettlements.length === 0 ? (
                <p className="settlements-empty">
                  Brak rozrachunków w wybranym zakresie dat.
                </p>
              ) : (
                <>
                  <div
                    className="settlements-table"
                    role="region"
                    aria-live="polite"
                  >
                    <div
                      className="settlements-cards"
                      role="region"
                      aria-live="polite"
                    >
                      {paginatedSettlements.map((invoice) => (
                        <SettlementCard
                          key={`card-${invoice.invoiceNumber}-${invoice.issuedAt}`}
                          invoice={invoice}
                          formatDate={formatDate}
                          formatCurrency={formatCurrency}
                          formatOverdue={formatOverdue}
                        />
                      ))}
                    </div>
                    <SettlementsTable
                      invoices={paginatedSettlements}
                      formatDate={formatDate}
                      formatCurrency={formatCurrency}
                      formatOverdue={formatOverdue}
                    />
                  </div>
                  {renderSettlementsPagination()}
                </>
              )}
            </>
          )}
        </section>
      ) : activePage === "profile" ? (
        <ProfileView user={user} onUserUpdate={onUserUpdate} />
      ) : null}
      <div
        className={`cart-overlay ${isCartVisible ? "visible" : ""}`}
        role="presentation"
        aria-hidden={!isCartVisible}
        onClick={() => setCartVisible(false)}
      />
      <button
        type="button"
        className={`floating-cart-toggle ${isCartVisible ? "hidden" : ""}`}
        onClick={() => setCartVisible((prev) => !prev)}
        aria-label={isCartVisible ? "Zamknij koszyk" : "Otwórz koszyk"}
      >
        <IconCart className="cart-toggle-icon" />
        {cartItems.length > 0 && <span>{cartItems.length}</span>}
      </button>
      <CartSummary
        items={cartItems}
        totalNet={cartTotals.net}
        totalGross={cartTotals.gross}
        totalDiscount={cartDiscount.amountNet}
        totalDiscountPercent={cartDiscount.percent}
        totalNetAfterDiscount={cartDiscount.netAfter}
        totalGrossAfterDiscount={cartDiscount.grossAfter}
        notes={notes}
        onNotesChange={setNotes}
        onSubmit={handleOrderSubmit}
        onRemoveItem={removeCartItem}
        onQuantityChange={handleCartQuantityChange}
        deliveryDate={deliveryDate}
        onDeliveryDateChange={handleDeliveryDateChange}
        minDeliveryDate={minDeliveryDate}
        existingOrderMessage={existingOrderMessage}
        hasExistingOrder={hasExistingOrder}
        isMobileFloating
        isMobileVisible={isCartVisible}
        onCloseMobile={() => setCartVisible(false)}
        status={status}
        onTouchStart={handleCartTouchStart}
        onTouchMove={handleCartTouchMove}
        onTouchEnd={handleCartTouchEnd}
      />
    </div>
  );
};

export default OrdersView;
